import { useEffect, useState } from "react";
import { scrollToId } from "@/lib/lenis";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BrandMark } from "@/components/site/BrandMark";
import { API } from "@/lib/api";
import { waChat, waMsg } from "@/components/site/WhatsAppFloat";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";

const LINKS = [
  { id: "inicio", label: "Início" },
  { id: "sobre", label: "Sobre" },
  { id: "especialidades", label: "Especialidades" },
  { id: "abordagem", label: "Abordagem" },
  { id: "horarios", label: "Horários" },
  { id: "contato", label: "Contato" },
];

export default function Navbar({ content }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const logoUrl = content?.logo_path ? `${API}/files/${content.logo_path}` : null;
  const hideName = !!content?.logo_hide_name && !!logoUrl;
  const waUrl = waChat(content?.whatsapp, waMsg(content));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#5B6E5B]/10 shadow-[0_8px_32px_rgba(91,110,91,0.06)]"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          onClick={() => go("inicio")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          {logoUrl ? (
            <img src={logoUrl} alt="Logo Tacilaine Almeida" className={`${hideName ? "h-10 sm:h-12" : "h-9 sm:h-10 w-9 sm:w-10 object-contain"} transition-transform duration-500 group-hover:scale-105`} data-testid="nav-logo-img" />
          ) : (
            <BrandMark className="w-9 h-9 sm:w-10 sm:h-10 transition-transform duration-500 group-hover:rotate-6" />
          )}
          {!hideName && (
            <span className="flex flex-col leading-none text-left">
              <span className="font-script text-2xl sm:text-3xl text-[#2C332A] -mb-1">Tacilaine Almeida</span>
              <span className="font-body text-[10px] tracking-[0.35em] text-[#7A8A7A] uppercase">Psicóloga</span>
            </span>
          )}
        </button>

        <ul className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <li key={l.id}>
              <button
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="relative font-body text-sm text-[#4A5D4E] hover:text-[#2C332A] transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-[#C87A5E] after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp"
            title="Falar no WhatsApp"
            aria-label="Falar no WhatsApp"
            className="hidden md:grid place-items-center w-10 h-10 mr-3 rounded-full border border-[#25D366]/45 text-[#1EBE5A] transition-[transform,background-color] duration-300 hover:bg-[#25D366]/10 hover:scale-105"
          >
            <WhatsAppIcon className="w-5 h-5" />
          </a>
        )}

        <button
          onClick={() => go("contato")}
          data-testid="nav-cta-desktop"
          className="hidden md:inline-flex items-center rounded-full bg-[#5B6E5B] px-6 py-2.5 text-sm text-[#FDFBF7] transition-[transform,background-color] duration-300 hover:bg-[#4A5D4E] hover:scale-[1.03]"
        >
          Agendar
        </button>

        <button
          className="md:hidden text-[#2C332A]"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden backdrop-blur-xl bg-[#FDFBF7]/95 border-b border-[#5B6E5B]/10"
            data-testid="nav-mobile-menu"
          >
            <ul className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => go(l.id)}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className="font-display text-2xl text-[#2C332A]"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => go("contato")}
                  data-testid="nav-cta-mobile"
                  className="mt-2 inline-flex rounded-full bg-[#5B6E5B] px-6 py-3 text-[#FDFBF7]"
                >
                  Agendar atendimento
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
